import { INTERVIEW_MODE_LABELS, type InterviewSummaryView } from '@nest-recruit/shared';

/** "First interview · 60 min · Online" — the line every interview row carries. */
export function roundLine(interview: Pick<InterviewSummaryView, 'roundName' | 'durationMinutes' | 'mode'>): string {
  return `${interview.roundName} · ${interview.durationMinutes} min · ${INTERVIEW_MODE_LABELS[interview.mode]}`;
}

/** Where it happens, for staff: the room, the link, or "no room yet". */
export function placeLine(interview: Pick<InterviewSummaryView, 'mode' | 'roomName' | 'meetingLink'>): string {
  if (interview.mode === 'online') return interview.meetingLink ? 'Online — link set' : 'Online — no link yet';
  if (interview.mode === 'phone') return 'By phone';
  return interview.roomName ?? 'In person — no room yet';
}

export function panelNames(interview: Pick<InterviewSummaryView, 'panel'>): string {
  return interview.panel.map((member) => member.fullName).join(', ') || 'No panel';
}

/** The local calendar day an instant falls on in the viewer's zone, as `YYYY-MM-DD` (for grouping). */
export function dayKey(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

/** "Sunday 04-10-2026" — a day heading (06-qol §11: DD-MM-YYYY). */
export function dayHeading(key: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return key;
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${key}T12:00:00Z`));
  return `${weekday} ${match[3]}-${match[2]}-${match[1]}`;
}

/** "Sun 04-10" — a column heading in the week. */
export function shortDay(key: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return key;
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${key}T12:00:00Z`));
  return `${weekday} ${match[3]}-${match[2]}`;
}

/** Groups rows by the day they start on, in order — the SCH-02 phone list and SCH-01's slots. */
export function byDay<T>(rows: readonly T[], startsAt: (row: T) => string | null, timeZone: string): readonly { readonly day: string; readonly rows: readonly T[] }[] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const iso = startsAt(row);
    if (!iso) continue;
    const key = dayKey(iso, timeZone);
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, list]) => ({ day, rows: list }));
}

/** A row's date tile (Redesign Group 7): "Thu", "22", "Oct" in the viewer's zone — no year, the list is near-term. */
export function dateTileParts(iso: string, timeZone: string): { readonly weekday: string; readonly day: string; readonly month: string } {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(new Date(iso));
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((item) => item.type === type)?.value ?? '';
  return { weekday: part('weekday'), day: part('day'), month: part('month') };
}
